import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import type { NavigateFunction } from 'react-router';
import dayjs from 'dayjs';
import { Button } from '@/kit/Button';
import { FormField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { RequiredMark } from '@/kit/RequiredMark';
import { ClosePopup, type ClosePopupData } from '@/kit/ClosePopup';
import { openDialog } from '@/kit/dialog';
import { notifyError } from '@/core/notify/notify';
import { selectedClient, type SelectedClient } from '@/core/selectedClient';
import { ExportFormatCode } from '@/core/models/practice.models';
import type { ManagementPeriodRow } from '@/core/models/management-reporting.models';

/**
 * Runs `init` once when the screen mounts (Angular `ngOnInit`). React StrictMode mounts effects twice in development;
 * the guard keeps the loads to one, like Angular.
 */
export function useOnInit(init: () => void): void {
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) {
      return;
    }
    ran.current = true;
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ngOnInit semantics: run once
  }, []);
}

/** The client of the Accounts screens, read once when the screen opens (Angular `this.client = practice.selectedClient()`). */
export function useSelectedClient(): SelectedClient | null {
  const [client] = useState(() => selectedClient());
  return client;
}

/** The bootstrap guard of every per-client screen. Returns false (after the toast and redirect) when there is no client. */
export function guardClient(client: SelectedClient | null, navigate: NavigateFunction): client is SelectedClient {
  if (!client) {
    notifyError('Select a client from Accounts first.');
    navigate('/home/accountclient');
    return false;
  }
  return true;
}

/**
 * State that is also readable synchronously, like a component field: `ref.current` always holds the latest value
 * (Angular handlers read `this.x` right after assigning it), while `state` drives the render.
 */
export function useLiveState<T extends object>(initial: T) {
  const [state, setState] = useState<T>(initial);
  const ref = useRef<T>(state);
  const set = useCallback((patch: Partial<T> | ((current: T) => Partial<T>)) => {
    const change = typeof patch === 'function' ? patch(ref.current) : patch;
    ref.current = { ...ref.current, ...change };
    setState(ref.current);
  }, []);
  return [state, set, ref as MutableRefObject<T>] as const;
}

/** The ClosePopup delete confirmation: true only on "Yes". */
export async function confirmDelete(itemName: string): Promise<boolean> {
  const confirmed = await openDialog<ClosePopupData, boolean>(ClosePopup, {
    data: { itemName, count: 1 },
  }).afterClosed();
  return !!confirmed;
}

/**
 * An RxJS `Subject.pipe(debounceTime(delay), distinctUntilChanged())` subscription: returns `next(text)`. The first
 * emission always passes; later ones only when they differ from the previous emission. Pending work is dropped on
 * unmount (`ngOnDestroy` unsubscribe).
 */
export function useDebouncedDistinct(delayMs: number, onEmit: (text: string) => void): (text: string) => void {
  const latest = useRef(onEmit);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const last = useRef<string | undefined>(undefined);

  useEffect(() => {
    latest.current = onEmit;
  });
  useEffect(() => () => clearTimeout(timer.current), []);

  return useCallback(
    (text: string) => {
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        if (last.current !== undefined && last.current === text) {
          return;
        }
        last.current = text;
        latest.current(text);
      }, delayMs);
    },
    [delayMs],
  );
}

/** Angular `DatePipe` in the default en-US locale for a date-ish string; '' for nothing. */
export function formatDate(value: string | null | undefined, format: string): string {
  if (!value) {
    return '';
  }
  return dayjs(value).locale('en').format(format);
}

/**
 * #183: when a management period's snapshot was taken, in local time. The API sends `snapshotAtUtc` without a zone,
 * so it is read as UTC.
 */
export function snapshotTime(snapshotAtUtc: string | null | undefined): string {
  if (!snapshotAtUtc) {
    return '';
  }
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(snapshotAtUtc);
  return formatDate(hasZone ? snapshotAtUtc : `${snapshotAtUtc}Z`, 'DD/MM/YYYY HH:mm');
}

/** #183: a management period's snapshot as the screens show it - when it was taken and how many lines it holds. */
export function snapshotSummary(period: Pick<ManagementPeriodRow, 'snapshotAtUtc' | 'snapshotLineCount'>): string {
  const taken = snapshotTime(period.snapshotAtUtc);
  return taken ? `${taken} · ${period.snapshotLineCount} lines` : 'Not taken';
}

/** A mandatory field with its red asterisk beside it (fields here carry a placeholder, not a label, so the mark sits after the box). */
export function RequiredWrap({ children }: { children: ReactNode }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'flex-start' }}>
      {children}
      <span style={{ marginLeft: 2, marginTop: 6 }}>
        <RequiredMark />
      </span>
    </span>
  );
}

interface MatButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** True while the action is running: the button is disabled so a second click cannot submit twice. */
  busy?: boolean;
  /** A leading `<mat-icon>` (a direct child of the button, as Material lays it out). */
  icon?: string;
  iconOutlined?: boolean;
  /** Extra class on the icon itself, e.g. `icon-color-pdf`/`icon-color-xls` for a file-export icon. */
  iconClassName?: string;
}

/**
 * `<button mat-button>` with an optional leading `<mat-icon>`. The kit `Button` nests everything in the label span;
 * Material puts the icon before it and the legacy styles select `.mat-mdc-button > .mat-icon`.
 */
export const MatButton = forwardRef<HTMLButtonElement, MatButtonProps>(function MatButton(
  { icon, iconOutlined = false, iconClassName, className = '', type = 'button', children, busy = false, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={`mdc-button mat-mdc-button mat-unthemed mat-mdc-button-base ${className}`.trim()}
      {...rest}
    >
      {icon && <Icon name={icon} outlined={iconOutlined} className={iconClassName} />}
      <span className="mdc-button__label">{children}</span>
    </button>
  );
});

/**
 * The "Export to Excel / CSV / PDF" toolbar buttons, direct and always visible (no more `more_vert` kebab/`mat-menu`).
 */
export function ExportMenu({ onExport }: { onExport: (format: ExportFormatCode) => void }) {
  return (
    <div className="toolbar">
      <Button title="Export to Excel" onClick={() => onExport(ExportFormatCode.Excel)}>
        <Icon name="border_all" outlined className="icon-color-xls" />
      </Button>
      <Button title="Export to CSV" onClick={() => onExport(ExportFormatCode.Csv)}>
        <Icon name="description" outlined className="icon-color-xls" />
      </Button>
      <Button title="Export to PDF" onClick={() => onExport(ExportFormatCode.Pdf)}>
        <Icon name="picture_as_pdf" outlined className="icon-color-pdf" />
      </Button>
    </div>
  );
}

/** Values of a dimension for the "All members" select (all of them, including inactive ones). */
export function dimensionValuesOf(
  dimensions: { code: string; values: { code: string; name: string }[] }[],
  code: string | null,
): { code: string; name: string }[] {
  const dimension = dimensions.find((d) => d.code === code);
  return dimension ? dimension.values.map((v) => ({ code: v.code, name: v.name })) : [];
}

/**
 * The logs' `<mat-form-field class="search-field"><mat-icon>search</mat-icon><input matInput placeholder="Search"/>`.
 * Controlled when `value` is given (email log, `[(ngModel)]`), otherwise a plain input (user log, `(input)`).
 */
export function SearchField({ value, onSearch }: { value?: string; onSearch: (text: string) => void }) {
  const [focused, setFocused] = useState(false);
  return (
    <FormField className="search-field" focused={focused}>
      <Icon name="search" />
      <input
        className="mat-mdc-input-element mdc-text-field__input mat-mdc-form-field-input-control"
        placeholder="Search"
        autoComplete="off"
        {...(value === undefined ? {} : { value })}
        onChange={(event) => onSearch(event.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
    </FormField>
  );
}
